import React, { useState, useEffect, useCallback, useMemo, lazy, Suspense, memo } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Navbar from './Components/layout/Navbar';
import Footer from './Components/layout/Footer';
import CardComponent from './Components/components/CardComponent';
import Features from './Components/components/Features';
import clients from './Components/components/ClientLogos';
import ErrorBoundary from './ErrorBoundary';
import projectsImage from './Assets/Images/LogoImages/palm-projects-basement-waterproofing-leakage-repairs.webp';
import certificationImage from './Assets/Images/LogoImages/cormix-distributors-galaxy-dubai-uae.webp';
import productsImage from './Assets/Images/ProductImages/cormix-h2o-stop-liquid.webp';
import AboutUsImage from './Assets/Images/CaseStudyImages/binghatti-basement-waterproofing-with-25-years-warranty.webp';
import IntegrationIcon from './Assets/Icons/mixing-liquid-crystalline-admixture-with-concrete.webp';
import JointIcon from './Assets/Icons/contite-seal-acci-construction-joint-galaxy-dubai.webp';
import PouringIcon from './Assets/Icons/pour-waterproofed-crystallised-concrete-on-site.webp';
import WhatsappIcon from './Assets/Icons/whatsapp.png';
import hero1 from './Assets/Images/DisplayImages/basement-crystalline-waterproofing-without-membranes.webp';
import hero2 from './Assets/Images/DisplayImages/crystalline-waterproofing-25-years-warranty.webp';
import hero3 from './Assets/Images/DisplayImages/crystalline-waterproofing-save-time.webp';
import hero4 from './Assets/Images/DisplayImages/liquid-crystalline-admixture.webp';
import { CONTACT_PHONE, CONTACT_EMAIL, OG_IMAGE, SITE_URL } from './constants';
import './styles/App.css';

// ── Module-level schema constants — computed once, never re-created on render ──

const HOME_BREADCRUMB_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL }],
});

const LOCAL_BUSINESS_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Galaxy Specialised Waterproofing CO. LLC",
  "image": OG_IMAGE,
  "@id": SITE_URL,
  "url": SITE_URL,
  "telephone": CONTACT_PHONE,
  "email": CONTACT_EMAIL,
  "priceRange": "$$",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Office No. 604, D2 Tower, Al Barsha 1",
    "addressLocality": "Dubai",
    "addressCountry": "AE",
  },
  "geo": { "@type": "GeoCoordinates", "latitude": 25.2231, "longitude": 55.2796 },
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
    "opens": "08:00",
    "closes": "18:00",
  },
  "description": "Galaxy Specialised Waterproofing is a leading crystalline waterproofing company in Dubai offering basement waterproofing, concrete leakage repair, and substructure waterproofing solutions with a 25-year warranty.",
  "areaServed": ["Dubai", "Abu Dhabi", "Sharjah", "UAE"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Waterproofing Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Concrete Substructure Waterproofing", "url": `${SITE_URL}/services/foundation-waterproofing` } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Concrete Leakage Repair", "url": `${SITE_URL}/services/leakage-repair` } },
    ],
  },
  "sameAs": ["https://www.cormix.com/"],
});

const FAQ_ITEMS = [
  {
    q: 'What is crystalline waterproofing?',
    a: 'Crystalline waterproofing uses active chemicals mixed directly into concrete at the batching plant. These chemicals react with moisture to grow insoluble crystals inside the concrete matrix, permanently blocking pores, capillaries, and hairline cracks — without any membrane. The crystals reactivate whenever moisture is present, making the structure self-healing for the life of the building.',
  },
  {
    q: 'Is crystalline waterproofing approved by Dubai Municipality (DCL)?',
    a: 'Yes. Galaxy Specialised Waterproofing uses Cormix H2O Stop Liquid, which holds full DCL (Dubai Central Laboratory) and ADQCC (Abu Dhabi Quality and Conformity Council) approvals — mandatory requirements for regulated construction projects across the UAE.',
  },
  {
    q: 'How much construction time does crystalline waterproofing save compared to membranes?',
    a: 'By eliminating the membrane application stage entirely, crystalline admixture waterproofing saves 30–40 working days per project. The concrete is waterproofed at the batching plant before it arrives on site — no separate crew, no curing delays.',
  },
  {
    q: 'What warranty does Galaxy Specialised Waterproofing offer?',
    a: 'Galaxy provides a 25-year product and service warranty on all crystalline waterproofing works, backed by Cormix International. The system is certified to BS 8102, ASTM C1202, and ACI standards.',
  },
  {
    q: 'What types of structures can be waterproofed with crystalline technology?',
    a: 'Crystalline admixture is suitable for basements, raft slabs, pile caps, retaining walls, foundations, water tanks, lift pits, and all below-ground concrete substructures. It is the preferred solution anywhere water infiltration is a risk in the UAE climate.',
  },
];

const FAQ_PAGE_SCHEMA = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQ_ITEMS.map(item => ({
    "@type": "Question",
    "name": item.q,
    "acceptedAnswer": { "@type": "Answer", "text": item.a },
  })),
});

const HomeFAQ = () => {
  const [openIndex, setOpenIndex] = React.useState(null);
  return (
    <section className="faq-section">
      <div className="faq-container">
        <h2 className="faq-heading">Frequently Asked Questions</h2>
        <p className="faq-subheading">Everything you need to know about crystalline waterproofing in Dubai</p>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className={`faq-item${openIndex === i ? ' open' : ''}`}>
              <button
                className="faq-question"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                aria-expanded={openIndex === i}
              >
                <span>{item.q}</span>
                <span className="faq-chevron" aria-hidden="true">{openIndex === i ? '−' : '+'}</span>
              </button>
              {openIndex === i && <div className="faq-answer"><p>{item.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Code-split page components — loaded only when navigated to
const About = lazy(() => import('./Components/pages/about-galaxy-crystalline-waterproofing'));
const Products = lazy(() => import('./Components/pages/crystalline-waterproofing-products-dubai-liquid-admixture'));
const ContactUs = lazy(() => import('./Components/pages/contact'));
const Services = lazy(() => import('./Components/pages/crystalline-waterproofing-services-dubai-leakage-repair'));
const Certification = lazy(() => import('./Components/pages/cormix-uae-certification'));
const FoundationWaterproofing = lazy(() => import('./Components/pages/concrete-substructure-waterproofing-basement-solutions'));
const ConcreteLeakageRepair = lazy(() => import('./Components/pages/concrete-leakage-repair-waterproofing'));

const StickyCTA = () => (
  <div className="sticky-cta">
    <a href={`tel:${CONTACT_PHONE}`} className="cta-button phone-button">
      <span className="cta-icon" aria-hidden="true">📞</span>
      <span className="cta-text">Call Now</span>
    </a>
    <a href={`https://wa.me/${CONTACT_PHONE}`} target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
      <img src={WhatsappIcon} alt="" aria-hidden="true" className="cta-icon" />
      <span className="cta-text">WhatsApp</span>
    </a>
  </div>
);

const HERO_IMAGES = [
  { src: hero1, alt: 'Basement crystalline waterproofing without membranes — Galaxy Dubai' },
  { src: hero2, alt: 'Crystalline waterproofing with 25 years warranty — Galaxy UAE' },
  { src: hero3, alt: 'Crystalline waterproofing saves 30–40 days on site' },
  { src: hero4, alt: 'Liquid crystalline admixture for concrete waterproofing' },
];

const HeroSlideshow = memo(() => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  return (
    <>
      {HERO_IMAGES.map((img, i) => (
        <img
          key={img.src}
          src={img.src}
          alt={img.alt}
          className={`hero-slide${i === current ? ' active' : ''}`}
          fetchPriority={i === 0 ? 'high' : undefined}
          loading={i === 0 ? 'eager' : 'lazy'}
          decoding={i === 0 ? 'sync' : 'async'}
        />
      ))}
    </>
  );
});

const ClientsSlider = memo(() => {
  const itemsPerSlide = 18;
  const totalSlides = Math.ceil(clients.length / itemsPerSlide);
  const [startIndex, setStartIndex] = useState(0);

  const nextSlide = useCallback(() => {
    setStartIndex((prev) => (prev + itemsPerSlide) % clients.length);
  }, []);
  const prevSlide = useCallback(() => {
    setStartIndex((prev) => (prev - itemsPerSlide + clients.length) % clients.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextSlide, 3500);
    return () => clearInterval(interval);
  }, [nextSlide]);

  const visibleClients = useMemo(
    () => [...clients, ...clients].slice(startIndex, startIndex + itemsPerSlide),
    [startIndex]
  );
  const activeIndex = Math.floor(startIndex / itemsPerSlide);

  return (
    <div className="clients-slider-container">
      <h2 className="clients-slider-heading">Trusted by Leading Clients and Consultants</h2>
      <div className="clients-slider-controls">
        <button className="slider-arrow" onClick={prevSlide} aria-label="Previous clients">‹</button>
        <div className="clients-slider-grid">
          {visibleClients.map((client, index) => (
            <div key={`${client.alt}-${index}`} className="client-card fade-in">
              <img src={client.src} alt={client.alt} loading="lazy" width="130" height="65" />
            </div>
          ))}
        </div>
        <button className="slider-arrow" onClick={nextSlide} aria-label="Next clients">›</button>
      </div>
      <div className="slider-dots">
        {[...Array(totalSlides)].map((_, idx) => (
          <span
            key={idx}
            className={`dot ${idx === activeIndex ? 'active' : ''}`}
            onClick={() => setStartIndex(idx * itemsPerSlide)}
          />
        ))}
      </div>
    </div>
  );
});

const WelcomeText = () => {
  const navigate = useNavigate();
  return (
    <div>
      <h1>Galaxy Specialised Waterproofing</h1>
      <p>Crystalline Waterproofing Company in Dubai — 25 Years Warranty</p>
      <button onClick={() => navigate('/contact')} className="contact-us-button">
        Contact Us
      </button>
    </div>
  );
};

const ConditionalImage = () => {
  const location = useLocation();
  return location.pathname === '/' ? (
    <div className="fullscreen-image">
      <HeroSlideshow />
      <div className="overlay">
        <WelcomeText />
      </div>
    </div>
  ) : null;
};

const Home = ({ cardData, handleReadMoreClick }) => {
  const firstThreeCards = cardData.slice(0, 3);

  return (
    <div className="home-content">
      <Helmet>
        <title>Crystalline Waterproofing Dubai | Galaxy Specialised Waterproofing | 25 Years Warranty</title>
        <meta name="description" content="Galaxy Specialised Waterproofing — leading crystalline waterproofing company in Dubai. We offer basement waterproofing, leakage repair, and concrete substructure solutions with a 25-year warranty. Authorised Cormix distributors in the UAE." />
        <meta name="keywords" content="crystalline waterproofing dubai, waterproofing companies in dubai, waterproofing contractors dubai, basement waterproofing dubai, concrete waterproofing uae, leakage repair dubai, cormix distributor uae, 25 year waterproofing warranty" />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={SITE_URL} />
        <meta property="og:title" content="Crystalline Waterproofing Dubai | Galaxy Specialised Waterproofing" />
        <meta property="og:description" content="Leading waterproofing company in Dubai. Crystalline basement waterproofing, leakage repair, and concrete substructure solutions with a 25-year warranty." />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:alt" content="Galaxy Specialised Waterproofing — Crystalline Waterproofing Company Dubai" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG_IMAGE} />
        <script type="application/ld+json">{FAQ_PAGE_SCHEMA}</script>
        <script type="application/ld+json">{HOME_BREADCRUMB_SCHEMA}</script>
        <script type="application/ld+json">{LOCAL_BUSINESS_SCHEMA}</script>
      </Helmet>

      <section className="intro-section">
        <div className="intro-container">
          <h2 className="intro-heading">Leading Crystalline Waterproofing Company in Dubai</h2>
          <p className="intro-text">
            Galaxy Specialised Waterproofing CO. LLC is the authorised distributor and applicator of Cormix crystalline waterproofing in the UAE. For over a decade, we have protected below-ground concrete structures across Dubai and Abu Dhabi — basements, raft slabs, foundations, and water tanks — using technology that eliminates membranes, saves 30–40 days on-site, and carries a 25-year warranty. Approved by DCL (Dubai Municipality) and ADQCC (Abu Dhabi).
          </p>
        </div>
      </section>

      <Features />

      <div className="process-section">
        <div className="process-container">
          <h2 className="section-title">Our Signature Waterproofing Process</h2>
          {[{
            icon: IntegrationIcon,
            title: '1. Admixture Integration at the Batching Plant',
            desc: 'Cormix H2O Stop liquid crystalline admixture is mixed with the concrete at the batching plant — no separate membrane application needed.',
          }, {
            icon: JointIcon,
            title: '2. Delivery & Pouring on Site',
            desc: 'Waterproofed concrete is delivered and poured directly on site, saving 30–40 days vs traditional membranes.',
          }, {
            icon: PouringIcon,
            title: '3. Construction Joint Treatment',
            desc: 'Construction joints are treated with Contite Seal ACCI and swellable bars for complete, long-lasting protection.',
          }].map((step) => (
            <div className="process-step" key={step.title}>
              <div className="process-step-header">
                <img src={step.icon} alt={step.title} className="process-icon" loading="lazy" width="36" height="36" />
                <h3>{step.title}</h3>
              </div>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card-container">
        {firstThreeCards.map((data) => (
          <CardComponent key={data.heading} cardData={data} />
        ))}
      </div>

      <div className="about-us-section">
          <div className="about-us-image">
            <img src={AboutUsImage} alt="Binghatti HQ basement waterproofing completed by Galaxy Specialised Waterproofing with 25-year warranty" loading="lazy" width="400" height="300" />
          </div>
          <div className="about-us-content">
            <h2 className="about-title">About Galaxy Specialised Waterproofing</h2>
            <p className="about-description">
              Galaxy Specialised Waterproofing CO LLC is the leading crystalline waterproofing company in Dubai, serving commercial and residential projects across the UAE with a 25-year warranty.
            </p>
            <button onClick={handleReadMoreClick} className="read-more-button">
              Read More
            </button>
          </div>
        </div>

      <div className="clients-section">
        <ClientsSlider />
      </div>

      <HomeFAQ />
    </div>
  );
};

function AppInner() {
  const navigate = useNavigate();

  const cardData = [
    { heading: 'Our Projects', description: 'See our completed waterproofing projects across Dubai.', image: projectsImage, link: '/services/foundation-waterproofing#few-case-studies' },
    { heading: 'Our Products', description: 'Browse our Cormix crystalline waterproofing products.', image: productsImage, link: '/products' },
    { heading: 'Certification', description: 'View our DCL and Cormix distributor certifications.', image: certificationImage, link: '/certification' },
  ];

  return (
    <div className="container">
      <Navbar />
      <ConditionalImage />
      <ErrorBoundary>
        <Suspense fallback={<div style={{ padding: '4rem', textAlign: 'center' }}>Loading…</div>}>
          <Routes>
            <Route path="/" element={<Home cardData={cardData} handleReadMoreClick={() => navigate('/about')} />} />
            <Route path="/about" element={<About />} />
            <Route path="/products" element={<Products />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/foundation-waterproofing" element={<FoundationWaterproofing />} />
            <Route path="/services/leakage-repair" element={<ConcreteLeakageRepair />} />
            <Route path="/certification" element={<Certification />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <Footer />
      <StickyCTA />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}

export default App;
